export const MIN_INTERVALS = 2;
export const MAX_INTERVALS = 6;
export const MAX_IMAGE_FILES = 20;
export const IMAGE_ACCEPT = ".jpg,.jpeg,.png,.bmp,.tiff,.tif";

const RESULT_META = {
  positivo: {
    label: "Positivo",
    color: "#dc3545",
    textColor: "white",
    icon: "mdi-plus-circle-outline",
  },
  negativo: {
    label: "Negativo",
    color: "#198754",
    textColor: "white",
    icon: "mdi-minus-circle-outline",
  },
  inconclusivo: {
    label: "Inconclusivo",
    color: "#ffc107",
    textColor: "black",
    icon: "mdi-alert-circle-outline",
  },
};

export function getIntervalName(index) {
  return index === 0 ? "Controle (C)" : `L${index}`;
}

function getIntervalBoundary(interval, keys) {
  for (const key of keys) {
    if (interval && interval[key] !== undefined && interval[key] !== null) {
      return interval[key];
    }
  }

  return null;
}

export function normalizeIntervals(intervals) {
  const source = Array.isArray(intervals) ? intervals : [];
  return source.map((interval, index) => ({
    name: interval.name || interval.itemName || getIntervalName(index),
    min: getIntervalBoundary(interval, ["min", "%min", "minPercent"]),
    max: getIntervalBoundary(interval, ["max", "%max", "maxPercent"]),
  }));
}

export function sanitizeIntervals(intervals) {
  return normalizeIntervals(intervals).map((interval) => ({
    name: interval.name,
    min: interval.min === "" || interval.min === null ? null : Number(interval.min),
    max: interval.max === "" || interval.max === null ? null : Number(interval.max),
  }));
}

// Deriva os intervalos a partir do teste: a posição 0 é sempre o Controle e as
// demais recebem os nomes do stripItemList de trás pra frente (o último item da
// lista é a linha mais próxima do controle). O itemName gravado em stripPositions
// é ignorado — registros antigos o têm na ordem errada.
export function stripTestToIntervals(stripTest) {
  const test = stripTest || {};
  const positions = Array.isArray(test.stripPositions) ? test.stripPositions : [];
  const items = Array.isArray(test.stripItemList) ? test.stripItemList : [];

  return sanitizeIntervals(
    positions.map((position, index) => {
      const item = index === 0 ? null : items[items.length - index];
      const name = index === 0 ? "Controle" : (item && item.itemName) || `L${index}`;
      return { ...position, name };
    })
  );
}

export function validateIntervals(intervals) {
  const normalized = sanitizeIntervals(intervals);

  if (normalized.length < MIN_INTERVALS) {
    return { valid: false, message: "Selecione um teste com controle e pelo menos uma linha de teste." };
  }

  if (normalized.length > MAX_INTERVALS) {
    return { valid: false, message: `Use no maximo ${MAX_INTERVALS} intervalos.` };
  }

  for (const interval of normalized) {
    if (!Number.isFinite(interval.min) || !Number.isFinite(interval.max)) {
      return { valid: false, message: `Preencha min e max de ${interval.name}.` };
    }

    if (interval.min < 0 || interval.min > 100 || interval.max < 0 || interval.max > 100) {
      return { valid: false, message: `${interval.name} deve ficar entre 0 e 100%.` };
    }

    if (interval.min >= interval.max) {
      return { valid: false, message: `${interval.name} precisa ter min menor que max.` };
    }
  }

  const orderedIntervals = [...normalized].sort((a, b) => a.min - b.min);

  for (let i = 1; i < orderedIntervals.length; i++) {
    const previous = orderedIntervals[i - 1];
    const current = orderedIntervals[i];

    if (current.min <= previous.max) {
      return { valid: false, message: `${current.name} toca ou se sobrepoe a ${previous.name}.` };
    }
  }

  return { valid: true, message: "" };
}

export function dataUriToFile(dataUri, filename) {
  const parts = dataUri.split(",");
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const binary = atob(parts[1]);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new File([bytes], filename, { type: mime });
}

export function buildResultFilename(filename, index) {
  const fallback = `imagem-${index + 1}`;
  const base = String(filename || fallback)
    .replace(/\.[^.]+$/, "")
    .replace(/[^\w.-]+/g, "-");

  return `${base || fallback}-resultado.png`;
}

export function getAiResultFiles(results) {
  const files = [];
  const errors = [];

  (results || []).forEach((result, index) => {
    if (result && result.ok && result.imageDataUri) {
      files.push(dataUriToFile(result.imageDataUri, buildResultFilename(result.filename, index)));
      return;
    }

    errors.push(`${result && result.filename ? result.filename : `Imagem ${index + 1}`}: ${result && result.error ? result.error : "falha no servico de IA"}`);
  });

  return { files, errors };
}

export function getResultKey(result) {
  const value = String(result || "inconclusivo").toLowerCase();
  return RESULT_META[value] ? value : "inconclusivo";
}

export function normalizeResultLabel(result) {
  return RESULT_META[getResultKey(result)].label;
}

export function getResultMeta(result) {
  return RESULT_META[getResultKey(result)];
}

export function getVisionLineResult(line) {
  if (line && line.result !== undefined && line.result !== null) {
    return getResultKey(line.result);
  }

  return line && line.positive ? "positivo" : "negativo";
}

export function formatRatio(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : "-";
}

export function formatBytes(bytes) {
  const size = Number(bytes || 0);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function isAiCompatibleFile(file) {
  const name = String(file && file.name ? file.name : "").toLowerCase();
  const type = String(file && file.type ? file.type : "").toLowerCase();
  return type === "image/jpeg" || type === "image/png" || /\.(jpe?g|png)$/i.test(name);
}

function buildMapResult(result) {
  const lines = result && result.diagRgb && result.diagRgb.lines ? result.diagRgb.lines : {};
  return Object.keys(lines).map((name) => ({
    item: name,
    value: Number(formatRatio(lines[name].ratio)),
    result: normalizeResultLabel(getVisionLineResult(lines[name])),
  }));
}

export function normalizeVisionResult(result, file, intervals, originalFile) {
  const rawResult = result || {};
  const filename = rawResult.filename || (file && file.name) || "imagem";

  return {
    ...rawResult,
    name: filename,
    testType: "Analise por visao",
    readingType: "IA",
    filename,
    finalResult: normalizeResultLabel(rawResult.finalResult),
    mapResult: buildMapResult(rawResult),
    imageFile: file || null,
    originalImageFile: originalFile || file || null,
    imageDataUri: rawResult.imageDataUri,
    rawVisionResult: rawResult,
    intervals: sanitizeIntervals(intervals),
  };
}
